import type { MouseEventHandler, ReactNode, RefObject } from 'react';

import CloseIcon from '@mui/icons-material/Close';
import HistoryOutlinedIcon from '@mui/icons-material/HistoryOutlined';
import { Box, Drawer } from '@mui/material';

import { Z_INDEX } from '@/config/z-index';

import { DRAWER_WIDTH } from './constants';
import { DrawerHeaderButton } from './DrawerHeaderButton';
import { DrawerHeaderBar } from './header';

export interface SideDrawerProps {
  open: boolean;
  onClose: MouseEventHandler;
  onXButtonClose?: MouseEventHandler;
  title: string;
  subtitle?: ReactNode;
  icon: ReactNode;
  onHistoryClick?: () => void;
  isHistoryActive?: boolean;
  historyButtonRef?: RefObject<HTMLElement | null>;
  headerAction?: ReactNode;
  before?: ReactNode;
  children?: ReactNode;
  width?: string | number;
}

const SideDrawer = ({
  open,
  onClose,
  onXButtonClose,
  title,
  subtitle,
  icon,
  onHistoryClick,
  isHistoryActive,
  historyButtonRef,
  headerAction,
  before,
  children,
  width = DRAWER_WIDTH
}: SideDrawerProps) => {
  return (
    <Drawer
      anchor='right'
      open={open}
      slotProps={{
        backdrop: {
          sx: {
            backgroundColor: 'rgba(18, 22, 63, 0.80)'
          }
        },
        paper: {
          sx: {
            width: `min(${width}, 100vw)`,
            display: 'flex',
            flexDirection: 'column',
            backgroundColor: 'gx.mainBackground'
          }
        }
      }}
      sx={{
        left: 'auto',
        zIndex: Z_INDEX.panel
      }}
      onClose={onClose}
    >
      <DrawerHeaderBar
        actions={
          <>
            {onHistoryClick && (
              <DrawerHeaderButton
                ref={historyButtonRef}
                active={Boolean(isHistoryActive)}
                label='Conversation history'
                onClick={onHistoryClick}
              >
                <HistoryOutlinedIcon />
              </DrawerHeaderButton>
            )}
            <DrawerHeaderButton
              label='Close'
              onClick={onXButtonClose ?? onClose}
            >
              <CloseIcon />
            </DrawerHeaderButton>
          </>
        }
        before={before}
        headerAction={headerAction}
        icon={icon}
        subtitle={subtitle}
        title={title}
      />
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden'
        }}
      >
        {children}
      </Box>
    </Drawer>
  );
};

export default SideDrawer;
